import { useState } from "react";
import { Loader2, Download, CheckCircle2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

const whitepaperSchema = z.object({
  name: z.string().trim().min(1, "Full name is required").max(100),
  company: z.string().trim().min(1, "Company is required").max(150),
  email: z.string().trim().email("Enter a valid work email").max(255),
});

type WhitepaperSubmission = {
  name: string;
  email: string;
  organisation: string;
  message: string;
  situation_type: string;
  source: "whitepaper";
  whitepaper_requested: boolean;
};

const typedSupabase = supabase as unknown as {
  from: (table: "contacts") => {
    insert: (values: WhitepaperSubmission[]) => Promise<{ error: { message: string; code?: string } | null }>;
  };
};

const inputClass =
  "h-11 rounded-[6px] border-form-border bg-background text-foreground placeholder:text-body/70 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0";
const labelClass = "text-[13px] font-semibold uppercase tracking-[0.1em] text-foreground";

const WHITEPAPER_PATH = "/truthforge-whitepaper.pdf";
const WHITEPAPER_FILENAME = "Truthforge-Whitepaper-May2026.pdf";

interface WhitepaperFormProps {
  variant?: "primary" | "secondary";
  headline?: string;
}

export const WhitepaperForm = ({ variant = "primary", headline }: WhitepaperFormProps) => {
  const [formData, setFormData] = useState({ name: "", company: "", email: "", website: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<"preparing" | "started" | "failed">("preparing");

  const triggerDownload = async () => {
    setDownloadStatus("preparing");
    try {
      const response = await fetch(WHITEPAPER_PATH);
      if (!response.ok) throw new Error("Failed to fetch whitepaper");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = WHITEPAPER_FILENAME;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
      setDownloadStatus("started");
    } catch {
      setDownloadStatus("failed");
    }
  };

  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData((current) => ({ ...current, [field]: value }));
    if (errorMessage) setErrorMessage(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);

    if (formData.website) {
      setSubmitted(true);
      return;
    }

    const parsed = whitepaperSchema.safeParse(formData);
    if (!parsed.success) {
      setErrorMessage(parsed.error.errors[0]?.message || "Please check the form and try again.");
      return;
    }

    setIsSubmitting(true);

    const { error } = await typedSupabase.from("contacts").insert([
      {
        name: parsed.data.name,
        email: parsed.data.email,
        organisation: parsed.data.company,
        situation_type: "Whitepaper Download",
        message: `Whitepaper download: ${parsed.data.name} at ${parsed.data.company}.`,
        source: "whitepaper",
        whitepaper_requested: true,
      },
    ]);

    setIsSubmitting(false);

    if (error) {
      if (error.code === "23505") {
        setSubmitted(true);
        void triggerDownload();
        return;
      }
      setErrorMessage("Something went wrong. Please try again or email us directly.");
      return;
    }

    setSubmitted(true);
    void triggerDownload();
  };

  if (submitted) {
    return (
      <div className="space-y-5 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
          <CheckCircle2 className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-black uppercase text-foreground">Your download is ready</h3>
          <p className="mx-auto max-w-sm text-sm leading-relaxed text-body">
            {downloadStatus === "preparing" && "Preparing your file…"}
            {downloadStatus === "started" && "It should have started automatically. If not, use the button below."}
            {downloadStatus === "failed" && "Click below to download."}
          </p>
        </div>
        <a
          href={WHITEPAPER_PATH}
          download={WHITEPAPER_FILENAME}
          onClick={(e) => {
            e.preventDefault();
            void triggerDownload();
          }}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-[6px] bg-amber px-6 font-bold text-amber-foreground transition hover:bg-amber/90"
        >
          <Download className="h-4 w-4" />
          Download the Whitepaper
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {headline && (
        <div className="space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Get the whitepaper</p>
          <h3 className="text-xl font-black uppercase text-foreground">{headline}</h3>
        </div>
      )}

      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden" style={{ position: "absolute" }}>
        <label htmlFor={`wp-website-${variant}`}>Website</label>
        <input
          id={`wp-website-${variant}`}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={formData.website}
          onChange={(e) => updateField("website", e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`wp-name-${variant}`} className={labelClass}>Full Name</Label>
        <Input id={`wp-name-${variant}`} value={formData.name} onChange={(e) => updateField("name", e.target.value)} required className={inputClass} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`wp-company-${variant}`} className={labelClass}>Company</Label>
        <Input id={`wp-company-${variant}`} value={formData.company} onChange={(e) => updateField("company", e.target.value)} required className={inputClass} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`wp-email-${variant}`} className={labelClass}>Work Email</Label>
        <Input id={`wp-email-${variant}`} type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} required className={inputClass} />
      </div>

      {errorMessage && (
        <div className="rounded-[6px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {errorMessage}
        </div>
      )}

      <Button type="submit" disabled={isSubmitting} className="h-12 w-full rounded-[6px] bg-amber text-base font-bold uppercase tracking-wide text-amber-foreground hover:bg-amber/90 disabled:opacity-60">
        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
        {isSubmitting ? "Preparing…" : "Download the Whitepaper"}
      </Button>

      <p className="text-center text-[11px] uppercase tracking-[0.16em] text-body">
        Instant PDF · 32 pages · Confidential
      </p>
    </form>
  );
};
